// @ts-nocheck
import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@libraryadmin/components/layout/DashboardLayout";
import { Button } from "@libraryadmin/components/ui/button";
import { usePagination } from "@libraryadmin/hooks/usePagination";
import { DataTablePagination } from "@libraryadmin/components/ui/data-table-pagination";
import { Input } from "@libraryadmin/components/ui/input";
import { Label } from "@libraryadmin/components/ui/label";
import { Textarea } from "@libraryadmin/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@libraryadmin/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@libraryadmin/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@libraryadmin/components/ui/dialog";
import { Badge } from "@libraryadmin/components/ui/badge";
import { Skeleton } from "@libraryadmin/components/ui/skeleton";
import {
  Save,
  Printer,
  FileDown,
  Search,
  Eye,
  Lock,
  Edit,
  Trash2,
  Plus,
  ScrollText,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  ChevronDown,
  Check,
} from "lucide-react";
import { useToast } from "@libraryadmin/hooks/use-toast";
import {
  getManuscripts,
  createManuscript,
  updateManuscript,
  deleteManuscript,
} from "@libraryadmin/services/api";
import type { Manuscript } from "@libraryadmin/data/seed";

// ─── Language constants ───────────────────────────────────────────────────────
const LANGUAGES = [
  "Gujarati",
  "English",
  "Hindi",
  "Sanskrit",
  "Prankit",
  "Urdu",
  "Marathi",
  "Kannada",
  "Pali",
  "Marwadi",
  "HE",
];

// ─── Manuscript Type constants ────────────────────────────────────────────────
const MANUSCRIPT_TYPES = [
  "અનુવાદ (Anuvad)",
  "મૂળ (Mul)",
  "ભાષાંતર (Bhashantar)",
  "ટીકા (Tika)",
  "સ્ટીક (Stik)",
  "સૂત્ર (Sutra)",
  "અવચૂર્ણ (Avachurna)",
  "નિર્યુક્તિ (Niryukti)",
  "ભાષ (Bhash)",
  "ઇત્તર (Ittar)",
  "ભાવાનુવાદ (Bhavanuvad)",
  "ટિપ્પણ (Tippan)",
  "દીપિકા (Dipika)",
];

// ─── Manuscript Category constants ───────────────────────────────────────────
const MANUSCRIPT_CATEGORIES = [
  "મેગઝીન (Magazine)",
  "હસ્તલિખિત પ્રત (Hastlikhit Prat)",
  "બુક (Book)",
  "પ્રત (Prat)",
  "સામિત્ર (Samitr)",
  "ડીરેક્ટરી (Directory)",
  "સ્મૃતિગ્રંથ (Smrutigrantha)",
  "Educational",
  "CD-DVD",
];

// ─── Multi-select checkbox dropdown component ─────────────────────────────────
interface MultiSelectProps {
  options: string[];
  selected: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
}

const MultiSelectLanguage = ({
  options,
  selected,
  onChange,
  placeholder = "Select languages",
}: MultiSelectProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (val: string) => {
    if (selected.includes(val)) {
      onChange(selected.filter(s => s !== val));
    } else {
      onChange([...selected, val]);
    }
  };

  const displayText =
    selected.length === 0
      ? placeholder
      : selected.length <= 2
        ? selected.join(", ")
        : `${selected.slice(0, 2).join(", ")} +${selected.length - 2} more`;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(p => !p)}
        className="input-field flex w-full items-center justify-between rounded-xl border border-border bg-background px-3 py-2 text-left text-sm transition-colors hover:bg-muted/30"
      >
        <span
          className={
            selected.length === 0
              ? "text-muted-foreground"
              : "font-medium text-foreground"
          }
        >
          {displayText}
        </span>
        <ChevronDown
          className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="absolute top-full z-50 mt-1 w-full overflow-hidden rounded-xl border border-border bg-popover shadow-xl">
          <div className="max-h-56 overflow-y-auto p-1">
            {options.map(option => {
              const checked = selected.includes(option);
              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => toggle(option)}
                  className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors hover:bg-accent/10 ${
                    checked ? "font-semibold text-primary" : "text-foreground"
                  }`}
                >
                  <span
                    className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border transition-colors ${
                      checked
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-background"
                    }`}
                  >
                    {checked && <Check className="h-3 w-3" />}
                  </span>
                  {option}
                </button>
              );
            })}
          </div>

          {selected.length > 0 && (
            <div className="flex items-center justify-between border-t bg-muted/30 px-3 py-2">
              <span className="text-xs text-muted-foreground">
                {selected.length} selected
              </span>
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-xs text-destructive hover:underline"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const Manuscripts = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Screen view mode
  const [viewMode, setViewMode] = useState<"list" | "add">("list");

  // Search & Filter States
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterLanguage, setFilterLanguage] = useState("all");
  const [filterDigitalised, setFilterDigitalised] = useState("all");

  // Dialog States (View / Edit / Delete remain as dialogs)
  const [viewingMs, setViewingMs] = useState<Manuscript | null>(null);
  const [editingMs, setEditingMs] = useState<Manuscript | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Form State for Add
  const initialForm: Omit<Manuscript, "id" | "status"> & {
    languages: string[];
    types: string[];
    categories: string[];
    topic: string;
    pdfLink: string;
    year: string;
    complete: string;
    imageFile: File | null;
  } = {
    manuscriptNo: "",
    title: "",
    author: "",
    mulkarta: "",
    tikakarta: "",
    era: "",
    script: "Devanagari",
    language: "",
    languages: [],
    types: [],
    categories: [],
    topic: "",
    pdfLink: "",
    year: "",
    complete: "",
    imageFile: null,
    pages: 0,
    availablePages: 0,
    condition: "Good",
    location: "",
    category: "",
    digitalised: false,
    restricted: false,
    remarks: "",
  };
  const [form, setForm] = useState(initialForm);

  // Queries & Mutations
  const { data: manuscripts = [], isLoading } = useQuery({
    queryKey: ["manuscripts"],
    queryFn: getManuscripts,
  });

  const createMutation = useMutation({
    mutationFn: createManuscript,
    onSuccess: newMs => {
      queryClient.invalidateQueries({ queryKey: ["manuscripts"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });
      toast({
        title: "Manuscript Added",
        description: `"${newMs.title}" (${newMs.manuscriptNo}) has been recorded.`,
      });
      setForm(initialForm);
      setViewMode("list");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Manuscript> }) =>
      updateManuscript(id, data),
    onSuccess: updated => {
      queryClient.invalidateQueries({ queryKey: ["manuscripts"] });
      toast({
        title: "Manuscript Updated",
        description: `"${updated.title}" details saved successfully.`,
      });
      setEditingMs(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteManuscript,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["manuscripts"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });
      toast({
        title: "Manuscript Deleted",
        description: "The manuscript record has been removed.",
        variant: "destructive",
      });
      setDeletingId(null);
    },
  });

  // Filtering Logic
  const filtered = manuscripts.filter(ms => {
    const matchesSearch =
      ms.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ms.manuscriptNo.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ms.mulkarta?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ms.tikakarta?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ms.author.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesCategory =
      filterCategory === "all" ||
      ms.category.toLowerCase() === filterCategory.toLowerCase();
    const matchesLanguage =
      filterLanguage === "all" ||
      ms.language.toLowerCase() === filterLanguage.toLowerCase();
    const matchesDigi =
      filterDigitalised === "all" ||
      (filterDigitalised === "yes" ? ms.digitalised : !ms.digitalised);

    return matchesSearch && matchesCategory && matchesLanguage && matchesDigi;
  });

  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.manuscriptNo || !form.title) {
      toast({
        title: "Validation Error",
        description: "Manuscript Number and Title are required.",
        variant: "destructive",
      });
      return;
    }
    const submitData = {
      ...form,
      language: form.languages.join(", ") || form.language,
    };
    createMutation.mutate(submitData);
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingMs) {
      return;
    }
    updateMutation.mutate({ id: editingMs.id, data: editingMs });
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExportCSV = () => {
    let csvContent =
      "Manuscript No,Title,Mulkarta,Tikakarta,Language,Script,Category,Condition,Digitalised,Restricted\n";
    filtered.forEach(m => {
      csvContent += `"${m.manuscriptNo}","${m.title}","${m.mulkarta || m.author}","${m.tikakarta || "-"}","${m.language}","${m.script}","${m.category}","${m.condition}","${m.digitalised ? "Yes" : "No"}","${m.restricted ? "Yes" : "No"}"\n`;
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "manuscripts_export.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const {
    currentPage,
    totalPages,
    itemsPerPage,
    paginatedData,
    handlePageChange,
    handleItemsPerPageChange,
  } = usePagination(filtered);

  return (
    <DashboardLayout
      title="Ancient Manuscripts Collection"
      subtitle="Preserve and catalog Jain Agamas, Granths, and rare sacred texts"
    >
      {/* ━━━ LIST VIEW ━━━ */}
      {viewMode === "list" && (
        <div className="animate-fade-in space-y-6">
          {/* Quick Stats Summary */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-xl border border-border/40 bg-card p-4 shadow-sm">
              <p className="text-xs font-medium text-muted-foreground">
                Total Manuscripts
              </p>
              <p className="mt-1 text-2xl font-bold text-primary">
                {isLoading ? "—" : manuscripts.length}
              </p>
            </div>
            <div className="rounded-xl border border-border/40 bg-card p-4 shadow-sm">
              <p className="text-xs font-medium text-muted-foreground">
                Digitised
              </p>
              <p className="mt-1 text-2xl font-bold text-success">
                {isLoading
                  ? "—"
                  : manuscripts.filter(m => m.digitalised).length}
              </p>
            </div>
            <div className="rounded-xl border border-border/40 bg-card p-4 shadow-sm">
              <p className="text-xs font-medium text-muted-foreground">
                Restricted Access
              </p>
              <p className="mt-1 text-2xl font-bold text-warning">
                {isLoading ? "—" : manuscripts.filter(m => m.restricted).length}
              </p>
            </div>
            <div className="rounded-xl border border-border/40 bg-card p-4 shadow-sm">
              <p className="text-xs font-medium text-muted-foreground">
                Damaged / Restoration
              </p>
              <p className="mt-1 text-2xl font-bold text-destructive">
                {isLoading
                  ? "—"
                  : manuscripts.filter(m => m.condition === "Damaged").length}
              </p>
            </div>
          </div>

          {/* Collection Table Section */}
          <div className="form-section">
            {/* Header Row */}
            <div className="mb-6 flex flex-col items-start justify-between gap-4 border-b pb-4 sm:flex-row sm:items-center">
              <div>
                <h2 className="text-lg font-bold text-foreground">
                  Manuscript Catalog
                </h2>
                <p className="text-xs text-muted-foreground">
                  Showing {filtered.length} of {manuscripts.length} manuscripts
                </p>
              </div>

              <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
                <Button
                  onClick={() => {
                    setForm(initialForm);
                    setViewMode("add");
                  }}
                  className="btn-primary w-full justify-center sm:w-auto"
                >
                  <Plus className="mr-2 h-4 w-4 shrink-0" /> Add Manuscript
                </Button>
                <div className="flex w-full items-center gap-2 sm:w-auto">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 justify-center rounded-lg sm:flex-initial"
                    onClick={handleExportCSV}
                  >
                    <FileDown className="mr-2 h-4 w-4 shrink-0" /> Export
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 justify-center rounded-lg sm:flex-initial"
                    onClick={handlePrint}
                  >
                    <Printer className="mr-2 h-4 w-4 shrink-0" /> Print
                  </Button>
                </div>
              </div>
            </div>

            {/* Filters Bar */}
            <div className="mb-6 grid grid-cols-1 gap-3 rounded-xl border border-border/50 bg-muted/30 p-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search title, author, MS no..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="input-field pl-9 text-xs"
                />
              </div>

              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="input-field h-9 text-xs">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="Agama">Agama</SelectItem>
                  <SelectItem value="Philosophy">Philosophy</SelectItem>
                  <SelectItem value="Cosmology">Cosmology</SelectItem>
                  <SelectItem value="Ethics">Ethics</SelectItem>
                  <SelectItem value="Logic">Logic</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterLanguage} onValueChange={setFilterLanguage}>
                <SelectTrigger className="input-field h-9 text-xs">
                  <SelectValue placeholder="Language" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Languages</SelectItem>
                  {LANGUAGES.map(l => (
                    <SelectItem key={l} value={l}>
                      {l}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filterDigitalised}
                onValueChange={setFilterDigitalised}
              >
                <SelectTrigger className="input-field h-9 text-xs">
                  <SelectValue placeholder="Digitised" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Digitised</SelectItem>
                  <SelectItem value="yes">Digitised Only</SelectItem>
                  <SelectItem value="no">Physical Only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Table */}
            <div className="overflow-hidden rounded-xl border border-border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Hastrap No</TableHead>
                    <TableHead>Granth Name &amp; Era</TableHead>
                    <TableHead>Mulkarta / Tikakarta</TableHead>
                    <TableHead>Language</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Digitised</TableHead>
                    <TableHead>Condition</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: 8 }).map((_, j) => (
                          <TableCell key={j}>
                            <Skeleton className="h-4 w-full" />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : filtered.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        className="py-12 text-center text-muted-foreground"
                      >
                        No manuscripts matching criteria found
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedData.map(ms => (
                      <TableRow key={ms.id} className="table-row-hover">
                        <TableCell className="font-mono text-xs font-semibold text-foreground">
                          {ms.manuscriptNo}
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm font-semibold text-primary">
                                {ms.title}
                              </span>
                              {ms.restricted && (
                                <Lock
                                  className="h-3 w-3 text-warning"
                                  title="Restricted Access"
                                />
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {ms.era}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell className="text-xs">
                          <div>
                            <p className="font-medium">
                              {ms.mulkarta || ms.author}
                            </p>
                            {ms.tikakarta && (
                              <p className="text-muted-foreground">
                                Tika: {ms.tikakarta}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs font-medium">
                          {ms.language}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className="border-secondary/20 bg-secondary/5 text-secondary"
                          >
                            {ms.category}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {ms.digitalised ? (
                            <Badge
                              variant="outline"
                              className="badge-success gap-1"
                            >
                              <CheckCircle className="h-3 w-3" /> Yes
                            </Badge>
                          ) : (
                            <Badge
                              variant="outline"
                              className="badge-destructive"
                            >
                              No
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={
                              ms.condition === "Excellent" ||
                              ms.condition === "Good"
                                ? "badge-info"
                                : "badge-warning"
                            }
                          >
                            {ms.condition}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setViewingMs(ms)}
                              title="View All Fields"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingMs(ms)}
                              title="Edit All Fields"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:bg-destructive/10"
                              onClick={() => setDeletingId(ms.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <DataTablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          </div>
        </div>
      )}

      {/* ━━━ ADD MANUSCRIPT FULL SCREEN ━━━ */}
      {viewMode === "add" && (
        <div className="animate-fade-in space-y-6">
          <form onSubmit={handleCreateSubmit}>
            <div className="form-section">
              {/* Header */}
              <div className="mb-8 flex items-center justify-between gap-4 border-b pb-4">
                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="rounded-xl"
                    onClick={() => {
                      setForm(initialForm);
                      setViewMode("list");
                    }}
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </Button>
                  <div>
                    <h2 className="flex items-center gap-2 text-lg font-bold text-foreground">
                      <ScrollText className="h-5 w-5 text-accent" />
                      Add Ancient Manuscript Record
                    </h2>
                    <p className="text-xs text-muted-foreground">
                      Catalog rare Jain Agamas, Prakrit scriptures, and
                      commentaries
                    </p>
                  </div>
                </div>
              </div>

              {/* Row 1: Hastrap No & Granth Title */}
              <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-3">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-foreground">
                    Hastrap / MS No <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    placeholder="e.g. MS011"
                    className="input-field font-mono"
                    value={form.manuscriptNo}
                    onChange={e =>
                      setForm({ ...form, manuscriptNo: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label className="text-sm font-semibold text-foreground">
                    Granth Name / Title{" "}
                    <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    placeholder="e.g. Uttaradhyayana Sutra"
                    className="input-field"
                    value={form.title}
                    onChange={e => setForm({ ...form, title: e.target.value })}
                  />
                </div>
              </div>

              {/* Row 2: Mulkarta, Tikakarta, Era */}
              <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-3">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-foreground">
                    Mulkarta (Original Author)
                  </Label>
                  <Input
                    placeholder="e.g. Bhadrabahu Swami"
                    className="input-field"
                    value={form.mulkarta}
                    onChange={e =>
                      setForm({
                        ...form,
                        mulkarta: e.target.value,
                        author: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-foreground">
                    Tikakarta (Commentator)
                  </Label>
                  <Input
                    placeholder="e.g. Silanka Acharya"
                    className="input-field"
                    value={form.tikakarta}
                    onChange={e =>
                      setForm({ ...form, tikakarta: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-foreground">
                    Era / Vikram Samvat Year
                  </Label>
                  <Input
                    placeholder="e.g. 3rd Century BCE"
                    className="input-field"
                    value={form.era}
                    onChange={e => setForm({ ...form, era: e.target.value })}
                  />
                </div>
              </div>

              {/* Row 3: Language (multi-select), Category, Condition */}
              <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-3">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    Language
                    <Badge
                      variant="outline"
                      className="border-accent/30 px-1.5 py-0 text-[10px] font-semibold text-accent"
                    >
                      Multi-select
                    </Badge>
                  </Label>
                  <MultiSelectLanguage
                    options={LANGUAGES}
                    selected={form.languages}
                    onChange={vals => setForm({ ...form, languages: vals })}
                    placeholder="Select language(s)"
                  />
                  {form.languages.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {form.languages.map(l => (
                        <span
                          key={l}
                          className="inline-flex items-center gap-1 rounded-full bg-accent/10 px-2 py-0.5 text-[11px] font-semibold text-accent"
                        >
                          {l}
                          <button
                            type="button"
                            onClick={() =>
                              setForm({
                                ...form,
                                languages: form.languages.filter(x => x !== l),
                              })
                            }
                            className="ml-0.5 text-accent hover:text-destructive"
                          >
                            x
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    Category
                    <Badge
                      variant="outline"
                      className="border-primary/40 px-1.5 py-0 text-[10px] font-semibold text-primary"
                    >
                      Multi-select
                    </Badge>
                  </Label>
                  <MultiSelectLanguage
                    options={MANUSCRIPT_CATEGORIES}
                    selected={form.categories}
                    onChange={vals =>
                      setForm({
                        ...form,
                        categories: vals,
                        category: vals.join(", "),
                      })
                    }
                    placeholder="Select category(s)"
                  />
                  {form.categories.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {form.categories.map(c => (
                        <span
                          key={c}
                          className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary"
                        >
                          {c}
                          <button
                            type="button"
                            onClick={() => {
                              const updated = form.categories.filter(
                                x => x !== c
                              );
                              setForm({
                                ...form,
                                categories: updated,
                                category: updated.join(", "),
                              });
                            }}
                            className="ml-0.5 text-primary hover:text-destructive"
                          >
                            x
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-foreground">
                    Condition
                  </Label>
                  <Select
                    value={form.condition}
                    onValueChange={v =>
                      setForm({
                        ...form,
                        condition: v as Manuscript["condition"],
                      })
                    }
                  >
                    <SelectTrigger className="input-field">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {["Excellent", "Good", "Fair", "Damaged"].map(c => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Row 3b: Type (multi-select) */}
              <div className="mb-6 grid grid-cols-1 gap-6">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    Type
                    <Badge
                      variant="outline"
                      className="border-secondary/40 px-1.5 py-0 text-[10px] font-semibold text-secondary"
                    >
                      Multi-select
                    </Badge>
                  </Label>
                  <MultiSelectLanguage
                    options={MANUSCRIPT_TYPES}
                    selected={form.types}
                    onChange={vals => setForm({ ...form, types: vals })}
                    placeholder="Select type(s) of manuscript"
                  />
                  {form.types.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {form.types.map(t => (
                        <span
                          key={t}
                          className="inline-flex items-center gap-1 rounded-full bg-secondary/10 px-2 py-0.5 text-[11px] font-semibold text-secondary"
                        >
                          {t}
                          <button
                            type="button"
                            onClick={() =>
                              setForm({
                                ...form,
                                types: form.types.filter(x => x !== t),
                              })
                            }
                            className="ml-0.5 text-secondary hover:text-destructive"
                          >
                            x
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Row 4b: Total Page, Available Page, Year */}
              <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-3">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-foreground">
                    Total Page
                  </Label>
                  <Input
                    type="number"
                    placeholder="e.g. 250"
                    className="input-field"
                    value={form.pages || ""}
                    onChange={e =>
                      setForm({ ...form, pages: Number(e.target.value) })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-foreground">
                    Available Page
                  </Label>
                  <Input
                    type="number"
                    placeholder="e.g. 200"
                    className="input-field"
                    value={form.availablePages || ""}
                    onChange={e =>
                      setForm({
                        ...form,
                        availablePages: Number(e.target.value),
                      })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-foreground">
                    Year
                  </Label>
                  <Input
                    type="number"
                    placeholder="e.g. 1985"
                    className="input-field"
                    value={form.year}
                    onChange={e => setForm({ ...form, year: e.target.value })}
                  />
                </div>
              </div>

              {/* Row 4c: Topic, PDF Link */}
              <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-foreground">
                    Topic
                  </Label>
                  <Input
                    placeholder="e.g. Jain Philosophy"
                    className="input-field"
                    value={form.topic}
                    onChange={e => setForm({ ...form, topic: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    PDF Link
                  </Label>
                  <Input
                    type="url"
                    placeholder="https://example.com/manuscript.pdf"
                    className="input-field font-mono text-xs"
                    value={form.pdfLink}
                    onChange={e =>
                      setForm({ ...form, pdfLink: e.target.value })
                    }
                  />
                </div>
              </div>

              {/* Row 4d: Complete + Select Image */}
              <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-foreground">
                    Completion Status
                  </Label>
                  <Select
                    value={form.complete}
                    onValueChange={v => setForm({ ...form, complete: v })}
                  >
                    <SelectTrigger className="input-field">
                      <SelectValue placeholder="Select completion status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Complete">Complete</SelectItem>
                      <SelectItem value="Incomplete">Incomplete</SelectItem>
                      <SelectItem value="Partial">Partial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-foreground">
                    Select Image
                  </Label>
                  <div className="relative">
                    <label
                      htmlFor="ms-image-upload"
                      className={`flex h-10 w-full cursor-pointer items-center justify-center gap-3 rounded-xl border-2 border-dashed px-4 transition-colors ${
                        form.imageFile
                          ? "border-accent/50 bg-accent/5 text-accent"
                          : "border-border/60 bg-muted/20 text-muted-foreground hover:border-border hover:bg-muted/40"
                      }`}
                    >
                      <svg
                        className="h-4 w-4 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <span className="truncate text-sm">
                        {form.imageFile
                          ? form.imageFile.name
                          : "Click to upload image"}
                      </span>
                    </label>
                    <input
                      id="ms-image-upload"
                      type="file"
                      accept="image/*"
                      className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                      onChange={e => {
                        const file = e.target.files?.[0] || null;
                        setForm({ ...form, imageFile: file });
                      }}
                    />
                  </div>
                  {form.imageFile && (
                    <div className="mt-1 flex items-center gap-2">
                      <img
                        src={URL.createObjectURL(form.imageFile)}
                        alt="Preview"
                        className="h-16 w-16 rounded-lg border border-border object-cover shadow-sm"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-xs font-semibold">
                          {form.imageFile.name}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          {(form.imageFile.size / 1024).toFixed(1)} KB
                        </p>
                        <button
                          type="button"
                          className="mt-0.5 text-[11px] text-destructive hover:underline"
                          onClick={() => setForm({ ...form, imageFile: null })}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Row 5: Checkboxes */}
              <div className="mb-6 flex items-center gap-8 rounded-xl border border-border/50 bg-muted/20 px-4 py-3">
                <label className="flex cursor-pointer items-center gap-3 text-sm font-medium">
                  <input
                    type="checkbox"
                    checked={form.digitalised}
                    onChange={e =>
                      setForm({ ...form, digitalised: e.target.checked })
                    }
                    className="h-4 w-4 rounded border-border accent-primary"
                  />
                  Digitised Scan Available
                </label>
                <label className="flex cursor-pointer items-center gap-3 text-sm font-medium text-warning">
                  <input
                    type="checkbox"
                    checked={form.restricted}
                    onChange={e =>
                      setForm({ ...form, restricted: e.target.checked })
                    }
                    className="h-4 w-4 rounded border-border accent-amber-500"
                  />
                  Restricted Access
                </label>
              </div>

              {/* Row 6: Remarks */}
              <div className="mb-6 space-y-2">
                <Label className="text-sm font-semibold text-foreground">
                  Remarks &amp; Notes
                </Label>
                <Textarea
                  placeholder="Preservation status, special notes..."
                  className="input-field min-h-[80px] resize-none"
                  value={form.remarks}
                  onChange={e => setForm({ ...form, remarks: e.target.value })}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-3 border-t pt-6">
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-xl"
                  onClick={() => {
                    setForm(initialForm);
                    setViewMode("list");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="btn-primary"
                  disabled={createMutation.isPending}
                >
                  <Save className="mr-2 h-4 w-4" />
                  {createMutation.isPending ? "Saving..." : "Save Manuscript"}
                </Button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* ━━━ View Detail Modal ━━━ */}
      <Dialog
        open={!!viewingMs}
        onOpenChange={open => !open && setViewingMs(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-primary">
              <ScrollText className="h-5 w-5 text-accent" />
              {viewingMs?.title}
            </DialogTitle>
            <DialogDescription className="font-mono text-xs">
              Hastrap No: {viewingMs?.manuscriptNo}
            </DialogDescription>
          </DialogHeader>

          {viewingMs && (
            <div className="space-y-3 py-2 text-xs">
              <div className="grid grid-cols-2 gap-2 rounded-lg border bg-muted/30 p-3">
                <div>
                  <span className="block text-muted-foreground">
                    Mulkarta (Author)
                  </span>
                  <span className="text-sm font-semibold">
                    {viewingMs.mulkarta || viewingMs.author || "---"}
                  </span>
                </div>
                <div>
                  <span className="block text-muted-foreground">
                    Tikakarta (Commentator)
                  </span>
                  <span className="text-sm font-semibold">
                    {viewingMs.tikakarta || "---"}
                  </span>
                </div>
                <div className="mt-2">
                  <span className="block text-muted-foreground">Language</span>
                  <span className="font-medium">{viewingMs.language}</span>
                </div>
                <div className="mt-2">
                  <span className="block text-muted-foreground">
                    Era / Period
                  </span>
                  <span>{viewingMs.era}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 rounded-lg border bg-card p-3">
                <div>
                  <span className="block text-muted-foreground">
                    Vault Location
                  </span>
                  <span className="font-mono font-semibold text-foreground">
                    {viewingMs.location}
                  </span>
                </div>
                <div>
                  <span className="block text-muted-foreground">
                    Folios / Pages
                  </span>
                  <span>
                    {viewingMs.availablePages || viewingMs.pages} /{" "}
                    {viewingMs.pages} folios
                  </span>
                </div>
                <div className="mt-2">
                  <span className="block text-muted-foreground">Category</span>
                  <Badge variant="outline">{viewingMs.category}</Badge>
                </div>
                <div className="mt-2">
                  <span className="block text-muted-foreground">Condition</span>
                  <Badge variant="outline">{viewingMs.condition}</Badge>
                </div>
              </div>

              <div className="flex gap-4 pt-1">
                <Badge
                  variant="outline"
                  className={
                    viewingMs.digitalised ? "badge-success" : "badge-secondary"
                  }
                >
                  Digitised: {viewingMs.digitalised ? "Yes" : "No"}
                </Badge>
                <Badge
                  variant="outline"
                  className={
                    viewingMs.restricted ? "badge-warning" : "badge-info"
                  }
                >
                  Access: {viewingMs.restricted ? "Restricted" : "Open Access"}
                </Badge>
              </div>

              {viewingMs.remarks && (
                <div className="rounded-md border border-amber-500/20 bg-amber-500/10 p-2.5 text-amber-800 dark:text-amber-300">
                  <strong>Remarks:</strong> {viewingMs.remarks}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setViewingMs(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ━━━ Edit Modal ━━━ */}
      <Dialog
        open={!!editingMs}
        onOpenChange={open => !open && setEditingMs(null)}
      >
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Manuscript Details</DialogTitle>
          </DialogHeader>
          {editingMs && (
            <form
              onSubmit={handleEditSubmit}
              className="space-y-4 py-2 text-xs"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Hastrap No</Label>
                  <Input
                    value={editingMs.manuscriptNo}
                    onChange={e =>
                      setEditingMs({
                        ...editingMs,
                        manuscriptNo: e.target.value,
                      })
                    }
                    className="input-field font-mono"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Title</Label>
                  <Input
                    value={editingMs.title}
                    onChange={e =>
                      setEditingMs({ ...editingMs, title: e.target.value })
                    }
                    className="input-field"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Mulkarta</Label>
                  <Input
                    value={editingMs.mulkarta || ""}
                    onChange={e =>
                      setEditingMs({ ...editingMs, mulkarta: e.target.value })
                    }
                    className="input-field"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Tikakarta</Label>
                  <Input
                    value={editingMs.tikakarta || ""}
                    onChange={e =>
                      setEditingMs({ ...editingMs, tikakarta: e.target.value })
                    }
                    className="input-field"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Era</Label>
                  <Input
                    value={editingMs.era}
                    onChange={e =>
                      setEditingMs({ ...editingMs, era: e.target.value })
                    }
                    className="input-field"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Language</Label>
                  <Select
                    value={editingMs.language}
                    onValueChange={v =>
                      setEditingMs({ ...editingMs, language: v })
                    }
                  >
                    <SelectTrigger className="input-field">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LANGUAGES.map(l => (
                        <SelectItem key={l} value={l}>
                          {l}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label>Category</Label>
                  <Select
                    value={editingMs.category}
                    onValueChange={v =>
                      setEditingMs({ ...editingMs, category: v })
                    }
                  >
                    <SelectTrigger className="input-field">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[
                        "Agama",
                        "Philosophy",
                        "Cosmology",
                        "Ethics",
                        "Logic",
                      ].map(c => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Location</Label>
                  <Input
                    value={editingMs.location}
                    onChange={e =>
                      setEditingMs({ ...editingMs, location: e.target.value })
                    }
                    className="input-field"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Condition</Label>
                  <Select
                    value={editingMs.condition}
                    onValueChange={v =>
                      setEditingMs({
                        ...editingMs,
                        condition: v as Manuscript["condition"],
                      })
                    }
                  >
                    <SelectTrigger className="input-field">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {["Excellent", "Good", "Fair", "Damaged"].map(c => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex gap-6 pt-2">
                <label className="flex cursor-pointer items-center gap-2 font-medium">
                  <input
                    type="checkbox"
                    checked={editingMs.digitalised}
                    onChange={e =>
                      setEditingMs({
                        ...editingMs,
                        digitalised: e.target.checked,
                      })
                    }
                    className="h-4 w-4 rounded border-border accent-primary"
                  />
                  Digitised Scan Available
                </label>
                <label className="flex cursor-pointer items-center gap-2 font-medium text-warning">
                  <input
                    type="checkbox"
                    checked={editingMs.restricted}
                    onChange={e =>
                      setEditingMs({
                        ...editingMs,
                        restricted: e.target.checked,
                      })
                    }
                    className="h-4 w-4 rounded border-border accent-amber-500"
                  />
                  Restricted Access
                </label>
              </div>

              <div className="space-y-1">
                <Label>Remarks</Label>
                <Textarea
                  value={editingMs.remarks || ""}
                  onChange={e =>
                    setEditingMs({ ...editingMs, remarks: e.target.value })
                  }
                  className="input-field min-h-[70px]"
                />
              </div>

              <DialogFooter className="mt-4 border-t pt-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setEditingMs(null)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={updateMutation.isPending}
                  className="btn-primary"
                >
                  {updateMutation.isPending ? "Saving..." : "Save Changes"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* ━━━ Delete Confirmation ━━━ */}
      <Dialog
        open={!!deletingId}
        onOpenChange={open => !open && setDeletingId(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" /> Delete Manuscript Record
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this manuscript record?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deletingId && deleteMutation.mutate(deletingId)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting..." : "Confirm Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default Manuscripts;
